import { isPresent } from '../../../utils/array/filter.utils';
import {
	type AddToCartPricedOption,
	type ProductWarrantiesQueryVariables,
	type Maybe,
	type CartItemFieldsFragment,
	type MassAddItemInput,
	type MassAddItemFieldsFragment,
	type ShippingMethodTypeEnum
} from '../../__generated__/graphql-client-types';
import { MASS_ADD_ERROR } from '../../constants/cart';
import { type Cart, type CartItem, type SaveForLaterCartItem, type ShippingOption } from '../../types/cart.types';
import { type Customer } from '../../types/customer.types';
import { type Selection } from '../../types/product-option.types';
import { type ProductWarranties } from '../../types/product.types';
import { formatPrice } from '../general-helper/general-helper';
import { defaultSerializer, serializeSelection } from '../product-helper/configuration/product-configuration.helper';

const MASS_ADD_ROW_INCREMENT = 5;

export type CartAddPricedOption = { pricedOptionPrice: number } & AddToCartPricedOption;

export type CartAddParams = {
	id: number; // cartItemId
	variantId: number;
	quantity: number;
	price: number;
	pricedOptions?: CartAddPricedOption[];
	bundleItems?: CartAddParams[];
	subItems?: CartAddParams[];
};

export type RecentCartItemChange = {
	id: number;
	variantId: number;
	quantity: number;
	totalPrice: number;
};

export type RecentCartChanges = {
	items: RecentCartItemChange[];
	totalItems: number;
	subtotal: number;
};

export type RecentCart = {
	changes: RecentCartChanges;
	capture: (nextSnapshot: CartAddParams) => void;
	reset: (initialSnapshot?: CartAddParams) => void;
};

const DEFAULT_RECENT_CHANGES: RecentCartChanges = { items: [], totalItems: 0, subtotal: 0 };

/**
 * Strategy that updates the current item properties with a new item for tracking recently cart item updates.
 */
const updateRecentCartItem = (currentItem: RecentCartItemChange, newItem: RecentCartItemChange): RecentCartItemChange => {
	return {
		...currentItem,
		quantity: currentItem.quantity + newItem.quantity,
		totalPrice: currentItem.totalPrice + newItem.totalPrice
	};
};

/**
 * Updates an existing recent cart item with a new one and returns the updated list.
 */
const updateListRecentCartItem = (
	items: RecentCartItemChange[],
	existingItem: RecentCartItemChange,
	newItem: RecentCartItemChange
): RecentCartItemChange[] => {
	return items.map((item) => (item.id === existingItem.id ? updateRecentCartItem(existingItem, newItem) : item));
};

/**
 * Calculate Totals for a given list of recent cart item changes
 */
export const calculateTotals = (items: RecentCartItemChange[]): Pick<RecentCartChanges, 'totalItems' | 'subtotal'> => {
	return items.reduce(
		(totals, item) => {
			totals.totalItems += item.quantity;
			totals.subtotal += item.totalPrice;
			return totals;
		},
		{ totalItems: 0, subtotal: 0 }
	);
};

/**
 * Update list of recent cart items by adding or modifying existing items with new information based on item ids.
 */
export const mergeCartItemChanges = (
	newRecentCartChanges: RecentCartChanges,
	currentRecentCartChanges: RecentCartChanges
): RecentCartItemChange[] => {
	return newRecentCartChanges.items.reduce((items, item) => {
		const existingItem = items.find((existing) => existing.variantId === item.variantId);
		return existingItem ? updateListRecentCartItem(items, existingItem, item) : [...items, item];
	}, currentRecentCartChanges.items);
};

export const mapWarrantyQueryVariables = (items: CartItem[]): ProductWarrantiesQueryVariables => {
	return {
		products: items
			.filter((item) => !item.isSubItem)
			// TODO: EFDC-28017 Remove once NewLeaf supports warranties for all categories
			.filter((item) => item.baseCategory === 'Appliances')
			// TODO: EFDC-28017 Remove once New Leaf supports warranties for all categories
			.filter((item) => item.type !== 'Warranty')
			.map((item) => ({
				modelNumber: item.modelNumber,
				title: item.title,
				price: item.price.unitPrice,
				manufacturer: item.brandName
			}))
	};
};

export const isLastItem = (id: number, items?: Maybe<CartItem[]>) => {
	if (!items) {
		return false;
	}

	return items[items.length - 1].id === id;
};

export const findCartItemById = (cartItems: CartItemFieldsFragment[], id: number): CartItemFieldsFragment | undefined => {
	return cartItems.find((cartItem) => cartItem.variant.id === id);
};

export const getRecentCartItemQuantity = (recentCartChanges: RecentCartChanges, cartItem: CartItemFieldsFragment): number => {
	const found = recentCartChanges.items.find((recentCartItem) => recentCartItem.id === cartItem.id);
	return found ? found.quantity : 1;
};

export const mapCartItemFragmentToCartItem = (fragment: CartItemFieldsFragment): CartItem => {
	return {
		...fragment,
		fulfillmentOptions: [],
		whiteGloveShippable: false,
		isLTL: false,
		priceOverrideMessage: null,
		application: '',
		baseCategory: '',
		type: '',
		collection: '',
		prop65Warning: null,
		ordinal: 0,
		containerType: '',
		hasTrilogiePrice: false,
		shipping: null,
		subItems: [],
		betterValueProduct: null
	};
};

/**
 *  Maps a bundle item to a cartAddParam type.
 */
export const mapBundleItemToCartAddParams = (bundleItem: CartItemFieldsFragment): CartAddParams => {
	const { id, quantity } = bundleItem;
	const price = bundleItem.price.unitPrice;
	const variantId = bundleItem.variant.id;
	return { id, variantId, quantity, price };
};

/**
 * Creates a Cart Add instance from a configuration selection and the result of an add to cart operation.
 */
export const createCartAdd = (selection: Selection, cartItems: CartItemFieldsFragment[]): CartAddParams | null => {
	const { id, price, quantity, isABundle } = selection;
	if (isABundle) {
		const bundleItemsCartAdd = cartItems.map<CartAddParams>((cartItem) => ({ ...mapBundleItemToCartAddParams(cartItem), quantity }));
		return { id, variantId: id, price, quantity, bundleItems: bundleItemsCartAdd };
	}
	const found = findCartItemById(cartItems, selection.id);
	const { pricedOptions, subItems } = serializeSelection(selection, (option) => {
		const pricedOption = defaultSerializer(option);
		return pricedOption ? { ...pricedOption, pricedOptionPrice: option.price } : null;
	});
	const subItemsCartAdd = cartItems
		.filter((cartItem) => cartItem.isSubItem)
		.map<CartAddParams | null>((cartItem) => {
			const subItemFound = subItems?.find((subItem) => subItem.variantId === cartItem.variant.id);
			return subItemFound
				? {
						id: cartItem.id,
						quantity: subItemFound?.quantity || 1,
						price: cartItem.price.unitPrice,
						variantId: cartItem.variant.id
				  }
				: null;
		})
		.filter(isPresent);
	return found ? { id: found.id, variantId: id, price, quantity, pricedOptions, subItems: subItemsCartAdd } : null;
};

/**
 * Generates recent cart item changes commonly used to capture a snapshot of an individual product recently added to the cart.
 */
export const generateRecentCartItemChange = (cartAdd: CartAddParams): RecentCartItemChange => {
	const { id, variantId, quantity, price, pricedOptions } = cartAdd;
	const totalPrice =
		quantity *
		(pricedOptions?.reduce((total, option) => {
			total += option.pricedOptionPrice;
			return total;
		}, price) || price);
	return { id, variantId, quantity, totalPrice };
};

/**
 * Generates recent cart changes commonly used to capture a snapshot of most recent products added to the cart based on
 * an Add To Cart operation.
 */
export const generateRecentCartChanges = (cartAdd?: CartAddParams): RecentCartChanges => {
	if (cartAdd) {
		const items = [
			generateRecentCartItemChange(cartAdd),
			...(cartAdd.bundleItems?.map(generateRecentCartItemChange) || []),
			...(cartAdd.subItems?.map(generateRecentCartItemChange) || [])
		];
		return { ...calculateTotals(items), items };
	}
	return DEFAULT_RECENT_CHANGES;
};

/**
 * Checks if the cart has at least one "package" (a package is one item
 * that contains several individual items)
 */
export const cartHasPackage = (items?: Maybe<CartItem[]>) => {
	return items?.some((item) => item.containerType.toLowerCase() === 'package');
};

/**
 * The Mass Add Tool displays 5 rows of fields at a time (ie. clicking Add More will
 * add 5 more rows). This helper takes care of displaying 5 rows at a time as well
 * as including fields that are populated with data.
 */
export const getMassAddItems = (numGroups: number, items: MassAddItemInput[]): MassAddItemInput[] => {
	const itemsToAdd = numGroups * MASS_ADD_ROW_INCREMENT - items.length;

	if (itemsToAdd) {
		for (let i = 0; i < itemsToAdd; i++) {
			items.push({ searchTerm: '', quantity: 1 });
		}
	}

	return items;
};

/**
 * Returns a user friendly mass add error message based on the error code
 */
export const getMassAddErrorMessage = (item: MassAddItemFieldsFragment): string => {
	switch (item.errorCode) {
		case MASS_ADD_ERROR.NOT_FOUND:
			return `No product was found for "${item.searchTerm}"`;
		case MASS_ADD_ERROR.DUPLICATE:
			return `We found more than one product for "${item.searchTerm}"`;
		case MASS_ADD_ERROR.PRICED_OPTIONS:
			return `"${item.searchTerm}" is a configurable item`;
		default:
			return '';
	}
};

export const hasProp65Item = (items?: CartItem[] | null, customer?: Customer): boolean => {
	return (customer?.location?.state.abbreviation === 'CA' && items?.some((item) => item.prop65Warning)) || false;
};

const sortItemsByDate = (items: CartItem[]): CartItem[] => {
	return items.sort((a, b) => {
		const timeA = new Date(a.shipping?.deliveryDateLow).getTime();
		const timeB = new Date(b.shipping?.deliveryDateLow).getTime();
		return timeA - timeB;
	});
};

export const isSubItemWarranty = (item: CartItem) => item.isSubItem && item.type.toLowerCase() === 'warranty';

export const shouldShowUndoRemove = (removedItem?: CartItem | null) => {
	return removedItem ? !isSubItemWarranty(removedItem) : true;
};

/**
 * Group items by parcel or freight
 *
 * Subitem relationship removed except for warranties
 */
export const groupItems = (type: 'PARCEL' | 'FREIGHT', cart?: Cart | null) => {
	const isLTL = type === 'FREIGHT';
	const items = [...(cart?.items || [])];
	// Group by parcel or freight but keep warranty subItems in same group as parent
	const itemsByGroup = items.filter((item) => {
		if (isSubItemWarranty(item)) {
			const parentItem = items.find((parentItem) => parentItem.id === item.parentId);
			return parentItem?.isLTL === isLTL;
		}
		return item.isLTL === isLTL;
	});

	// Remove subItems that are warranties and add them back after sort
	const itemsWithoutWarranties = itemsByGroup.filter((item) => !isSubItemWarranty(item)).map((item) => ({ ...item, isSubItem: false }));

	const quickshipItems = sortItemsByDate(itemsWithoutWarranties.filter((item) => item.shipping?.isQuickShip));
	const estimatedDeliveryItems = sortItemsByDate(
		itemsWithoutWarranties.filter((item) => item.shipping?.message.toLowerCase().includes('estimated delivery'))
	);
	const otherItems = sortItemsByDate(
		itemsWithoutWarranties.filter(
			(item) => !item.shipping?.isQuickShip && !item.shipping?.message.toLowerCase().includes('estimated delivery')
		)
	);
	const sortedItems = [...quickshipItems, ...estimatedDeliveryItems, ...otherItems];

	// Add subItem warranties back
	itemsByGroup
		.filter((item) => isSubItemWarranty(item))
		.forEach((warranty) => {
			const parentIndex = sortedItems.findIndex((item) => item.id === warranty.parentId);
			sortedItems.splice(parentIndex + 1, 0, warranty);
		});

	return sortedItems;
};

export const getWhiteGloveCost = (cart?: Cart | null) => {
	const freightMethods = cart?.shippingDetails?.shippingOptions?.filter((option) => option.isLTL) || [];
	const whiteGloveShippingMethod = freightMethods.find((option) => option.isWhiteGlove);
	const freightShippingMethod = freightMethods.find((option) => !option.isWhiteGlove);

	return whiteGloveShippingMethod ? whiteGloveShippingMethod.price - (freightShippingMethod?.price || 0) : null;
};

export const getWarrantyText = (item: SaveForLaterCartItem, items: SaveForLaterCartItem[]) => {
	const warranty = items.find((currentItem) => currentItem.parentId === item.id && currentItem.type.toLowerCase() === 'warranty');

	return warranty ? `${warranty.title.substring(0, 6)} Protection Plan - ${formatPrice(warranty.price.unitPrice)}` : undefined;
};

/**
 * Validate shipping cost override amount
 *
 * Returns error message if amount is less than 0 or greater than shipping cost
 */
export const validateShippingCostOverride = (
	value: string,
	type: ShippingMethodTypeEnum,
	highCostShipping?: boolean,
	selectedShippingOption?: ShippingOption
) => {
	let error = '';
	const overrideAmount = Number(value);
	const isPositiveNumber = overrideAmount >= 0;

	if (overrideAmount < 0) {
		error = 'Cost override cannot be negative.';
	} else if ((!highCostShipping || type === 'PARCEL') && selectedShippingOption && overrideAmount > selectedShippingOption.price) {
		// Override cost can only be greater than shipping cost when high cost zip with freight
		error = 'Cost override cannot be greater than shipping cost.';
	} else if (!isPositiveNumber) {
		error = 'Please enter a valid number.';
	}

	return error;
};

/**
 * Validate the rebateTitle being present and return an cart item rebate an array
 *
 * Returns undefined if there's no rebates object or an empty array
 */
export const findRebateTitles = (items: CartItemFieldsFragment[]) => {
	return items
		?.map((item) => {
			return item?.rebates?.find((rebate) => rebate?.rebateTitle && rebate.rebateTitle?.length > 0);
		})
		.filter((rebateTitle) => rebateTitle !== undefined);
};

export type PreSelectedWarranty = {
	warrantyItemId: number;
	preSelectedWarrantyId: number;
	warrantySubItem: CartItem;
	warrantySubItemIndex: number;
};

/**
 * Finds a pre-selected warranty for a given cart item
 *
 * @param item - The cart item to find warranty for
 * @param cartItems - All cart items to search through
 * @returns PreSelectedWarranty if found, undefined otherwise
 */
export const findPreSelectedWarranty = (item: CartItem, cartItems: CartItem[]): PreSelectedWarranty | undefined => {
	const warrantyItemIndex = cartItems.findIndex((cartItem) => cartItem.parentId === item.id && cartItem.type === 'Warranty');

	if (warrantyItemIndex === -1) {
		return undefined;
	}

	const warrantyItem = cartItems[warrantyItemIndex];
	return {
		warrantyItemId: warrantyItem.id,
		preSelectedWarrantyId: warrantyItem.variant.id,
		warrantySubItem: warrantyItem,
		warrantySubItemIndex: warrantyItemIndex
	};
};

/**
 * Finds a product warranty for a given cart item from warranty data
 *
 * @param item - The cart item to find warranty for
 * @param productWarranties - Array of product warranties to search through
 * @returns ProductWarranties if found, undefined otherwise
 */
export const findProductWarranty = (item: CartItem, productWarranties?: ProductWarranties[]): ProductWarranties | undefined => {
	return productWarranties?.find((warranty) => warranty.modelNumber === item.modelNumber && !item.isSubItem);
};

/**
 * Formats a warranty title with price and quantity information
 *
 * @param description - The warranty description
 * @param price - The warranty price per unit
 * @param quantity - The quantity of warranties (optional, defaults to 1)
 * @returns Formatted warranty title string
 */
export const formatWarrantyTitle = (description: string, price: number, quantity?: number): string => {
	const safeQuantity = quantity ?? 1;
	const displayQuantity = safeQuantity > 1 ? `(Qty. ${quantity})` : '';
	return `${description} Protection Plan - ${formatPrice(price * safeQuantity)} ${displayQuantity}`;
};

/**
 * Finds cart items by populating warranty properties on parent items
 * when they have associated warranty sub-items
 *
 * @param cartItems - Array of cart items
 * @returns Array of cart items with warranty properties populated on parent items
 */
export const findCartItemsWithWarranty = (
	cartItems: CartItem[]
): (CartItem & {
	warrantyTitle?: string;
	warrantyQuantity?: number;
	warrantyUnitPrice?: number;
})[] => {
	return cartItems.map((item) => {
		if ('warrantyTitle' in item && item.warrantyTitle) {
			return item;
		}

		const warrantySubItem = cartItems.find((subItem) => subItem.parentId === item.id && subItem.type === 'Warranty');

		if (warrantySubItem) {
			const warrantyDescription = warrantySubItem.title || '';
			return {
				...item,
				warrantyQuantity: warrantySubItem.quantity,
				warrantyTitle: warrantyDescription.replace(/ Protection Plan$/, ''),
				warrantyUnitPrice: warrantySubItem.price.unitPrice
			};
		}

		return item;
	});
};
